import { FlowsErrorCodes, type TFlowsErrorCode } from '../constants/flows.error-codes';
import type { TFlowNode } from '../schemas/flow-graph.schema';
import { LibraryPromptPinSchema } from '../schemas/node-config.schemas';
import { PromptMessagesSchema } from '../services/flow-prompt';
import {
  AgentConfigSchema,
  AgentLimitsSchema,
  AgentMemorySchema,
  AgentToolSchema,
  FINAL_ANSWER_TOOL,
  type TAgentTool,
} from './agent-config.schema';
import { hostIsFixed, toolFieldRefs } from './agent-templates';
import { effectiveFields, HOOKPIPE_ACTION_PARAMS, toolChangesData } from './agent-tools';

/** `publish`: a warning while the draft is being written, an error when it goes live. */
export type TAgentFindingLevel = 'error' | 'warning' | 'publish';

export interface IAgentFinding {
  nodeKey: string;
  code: TFlowsErrorCode;
  message: string;
  level: TAgentFindingLevel;
  field?: string;
}

const C = FlowsErrorCodes;

function strings(value: unknown, at: string, visit: (text: string, at: string) => void): void {
  if (typeof value === 'string') visit(value, at);
  else if (Array.isArray(value)) value.forEach((item, i) => strings(item, `${at}.${i}`, visit));
  else if (value !== null && typeof value === 'object') {
    for (const [key, item] of Object.entries(value)) strings(item, `${at}.${key}`, visit);
  }
}

/**
 * The `ai.agent` rules of §3.3 that need nothing but the node; the ones that read the
 * workspace (the account's app, prices, Run a flow's target) come from the API as findings.
 */
export function agentFindings(node: TFlowNode): IAgentFinding[] {
  const config = readableAgentConfig(node.config ?? {});
  const found: IAgentFinding[] = [];
  const add = (code: TFlowsErrorCode, level: TAgentFindingLevel, message: string, field?: string) =>
    found.push({ nodeKey: node.key, code, level, message, ...(field ? { field } : {}) });

  if (!config.credentialId || !config.model) {
    add(C.AGENT_PICK_MODEL, 'publish', 'Pick a model and credentials', 'credentialId');
  }
  if (config.memory.mode !== 'off') {
    add(
      C.AGENT_MEMORY_UNAVAILABLE,
      'publish',
      'Conversation memory is not available yet — set Memory to off',
      'memory.mode',
    );
  }
  const instructions = [
    ...(config.messages ?? []).map((m, i) => ({ text: m.content, at: `messages.${i}.content` })),
    ...Object.entries(config.library?.vars ?? {}).map(([name, text]) => ({
      text,
      at: `library.vars.${name}`,
    })),
  ];
  for (const { text, at } of instructions) {
    if (toolFieldRefs(text).length > 0) {
      add(
        C.AGENT_INSTRUCTIONS_TOOL_ROOT,
        'error',
        'Instructions cannot read {{tool.…}} — only a tool’s own fields can',
        at,
      );
    }
  }
  config.tools.forEach((tool, index) => {
    if (tool) toolFindings(tool, `tools.${index}`, add);
  });
  return found;
}

export interface IReadableAgentConfig {
  credentialId?: string;
  model?: string;
  memory: { mode: string };
  messages?: { content: string }[];
  library?: { vars: Record<string, string> };
  limits: { maxCostUsd: number | null };
  tools: (TAgentTool | null)[];
}

// A config that fails the schema still gets its findings, part by part: the schema says what
// is malformed, these say the rest, and a half-written tool is left to the schema alone.
export function readableAgentConfig(raw: Record<string, unknown>): IReadableAgentConfig {
  const whole = AgentConfigSchema.safeParse(raw);
  if (whole.success) return whole.data;
  const text = (value: unknown) =>
    typeof value === 'string' && value.trim() !== '' ? value : undefined;
  const messages = PromptMessagesSchema.safeParse(raw.messages);
  const library = LibraryPromptPinSchema.safeParse(raw.library);
  const memory = AgentMemorySchema.safeParse(raw.memory ?? {});
  const limits = AgentLimitsSchema.safeParse(raw.limits ?? {});
  return {
    credentialId: text(raw.credentialId),
    model: text(raw.model),
    memory: memory.success ? memory.data : { mode: 'off' },
    limits: limits.success ? limits.data : { maxCostUsd: null },
    ...(messages.success ? { messages: messages.data } : {}),
    ...(library.success ? { library: library.data } : {}),
    tools: Array.isArray(raw.tools)
      ? raw.tools.map((tool: unknown) => {
          const one = AgentToolSchema.safeParse(tool);
          return one.success ? one.data : null;
        })
      : [],
  };
}

function toolFindings(
  tool: TAgentTool,
  at: string,
  add: (code: TFlowsErrorCode, level: TAgentFindingLevel, message: string, field?: string) => void,
): void {
  const label = `Tool ${tool.name}`;
  if (tool.name === FINAL_ANSWER_TOOL) {
    add(
      C.AGENT_TOOL_NAME_RESERVED,
      'error',
      `${FINAL_ANSWER_TOOL} is the agent’s own answer — pick another name`,
      `${at}.name`,
    );
  }
  if (tool.mode === 'ask') {
    add(
      C.AGENT_ASK_UNAVAILABLE,
      'publish',
      `${label}: “Ask before running” is not available yet — run it automatically or remove it`,
      `${at}.mode`,
    );
  } else if (toolChangesData(tool)) {
    add(
      C.AGENT_TOOL_CHANGES_DATA_AUTO,
      'warning',
      `${label} changes data and runs on its own, without asking anyone`,
      `${at}.mode`,
    );
  }
  const fields = effectiveFields(tool);
  const names = new Set(fields.map((field) => field.path));
  tool.fields.forEach((field, i) => {
    if (field.fill === 'fixed' && (field.value === undefined || field.value === '')) {
      add(
        C.AGENT_TOOL_FIELD_UNSET,
        'publish',
        `${label}: give ${field.path} a value`,
        `${at}.fields.${i}.value`,
      );
    }
    if (tool.kind === 'hookpipe') {
      const known = HOOKPIPE_ACTION_PARAMS[tool.config.action].some((p) => p.path === field.path);
      if (!known) {
        add(
          C.AGENT_TOOL_FIELD_UNKNOWN,
          'error',
          `${label}: ${tool.config.action} has no field ${field.path}`,
          `${at}.fields.${i}.path`,
        );
      }
    }
  });
  if (tool.kind !== 'action') return;
  strings(tool.config.input, `${at}.config.input`, (text, where) => {
    for (const name of toolFieldRefs(text)) {
      if (!names.has(name)) {
        add(
          C.AGENT_TOOL_FIELD_UNKNOWN,
          'error',
          `${label}: {{tool.${name}}} is not one of its fields`,
          where,
        );
      }
    }
  });
  const url = tool.config.input.url;
  if (tool.config.connector === 'http' && typeof url === 'string' && !hostIsFixed(url)) {
    add(
      C.AGENT_TOOL_HOST_NOT_FIXED,
      'error',
      `${label}: write the address up to its path — only the path and query may use the agent’s fields`,
      `${at}.config.input.url`,
    );
  }
}
